/**
 * Parsea texto extraído de un recibo/ticket para obtener el monto total.
 * Soporta formatos comunes: S/ 45.50, $ 45.50, TOTAL: 45.50, etc.
 */

// Patrones para montos: S/ 45.50, S/. 45.50, $ 45.50, 45.50, 1,234.56
const AMOUNT_REGEX = /(?:S\/?\.?|USD|\$)\s*([\d,]+(?:\.\d{1,2})?)|([\d,]+(?:\.\d{1,2})?)\s*(?:S\/?\.?|USD|\$)?/gi;

// Palabras que indican "total" en español e inglés
const TOTAL_KEYWORDS = /\b(total|total a pagar|total pagar|total general|amount|grand total|suma total)\b/i;

/**
 * Extrae el valor numérico de un string (ej: "45.50" o "1,234.56")
 */
function parseAmountString(str) {
  if (!str || typeof str !== "string") return null;
  const cleaned = str.replace(/,/g, "").trim();
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : num;
}

/**
 * Extrae todos los montos del texto
 */
function extractAmounts(text) {
  const amounts = [];
  let match;
  const regex = new RegExp(AMOUNT_REGEX.source, "gi");
  while ((match = regex.exec(text)) !== null) {
    const value = match[1] || match[2];
    const num = parseAmountString(value);
    if (num !== null && num > 0 && num < 10000000) {
      amounts.push(num);
    }
  }
  return amounts;
}

/**
 * Busca el monto total en el texto del recibo.
 * Prioriza líneas que contengan "total" y devuelve el monto más probable.
 */
export function parseReceiptAmount(text) {
  if (!text || typeof text !== "string") return null;

  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  let bestAmount = null;
  let totalLineAmount = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const hasTotalKeyword = TOTAL_KEYWORDS.test(line);
    const amounts = extractAmounts(line);

    if (hasTotalKeyword && amounts.length > 0) {
      // La línea con "total" suele tener el monto final
      totalLineAmount = amounts[amounts.length - 1];
    }

    if (amounts.length > 0 && !bestAmount) {
      bestAmount = amounts[amounts.length - 1];
    }
  }

  // Priorizar el monto de la línea "total"
  if (totalLineAmount !== null) return totalLineAmount;
  if (bestAmount !== null) return bestAmount;

  // Fallback: buscar cualquier monto en todo el texto
  const allAmounts = extractAmounts(text);
  if (allAmounts.length > 0) {
    return Math.max(...allAmounts);
  }

  return null;
}

/**
 * Detecta la moneda del recibo (USD o PEN).
 * Prioriza US$/USD sobre S/ para recibos que muestren ambos.
 */
export function parseReceiptCurrency(text) {
  if (!text || typeof text !== "string") return null;
  const t = text.toUpperCase();
  if (/\bUS\s*\$|\bUSD\b/.test(t)) return "USD";
  if (/S\/\.?|PEN\b/.test(t)) return "PEN";
  return null;
}
